import {
  APPL,
  ATC,
  CART,
  ADD_TO_CART_CLICK,
  CART_ADD,
  CART_ADD_LOCATION,
  HDPP_CART_ADD,
  OVERLAY,
  PARTS_AND_SERVICES,
  HDPP_OPTIONS_DRAWER,
  BOPIS_PARTS_AND_SERVICES,
  NOT_APPLICABLE,
  STASH,
  APPL_HDPP_CART_ADD,
  APPL_INSTALL_AND_HAUL_CART_ADD,
  BUNDLE_APPL_P_S_CART_ADD,
  PACKAGE_APPL_P_S_CART_ADD
} from '../constants';
import { getState } from '../analytics-state';

// The selected HDPP plan, as the drawer's protectionPlan AddOn ({ configAttr, price, ... }).
// configAttr is the plan SKU. Returns null when no plan was added or the payload predates it.
export const getSelectedProtectionPlan = (ele) => {
  const plan = ele?.protectionPlan;
  if (!ele?.hasProtection || !plan?.configAttr) {
    return null;
  }
  return {
    sku: String(plan.configAttr),
    price: plan.price
  };
};

const ADD_ON_PART = 'Part';
const ADD_ON_SERVICE = 'Service';

// The parts and haul/move services added for one appliance, from the drawer's
// installationAddOns and haulAwayAddOns AddOn lists ({ configAttr, price, type, ... }).
// Only Part and Service add-ons carry a SKU; the applianceConfig entry ("Hookup") is skipped.
// Returns [] when nothing was added or the payload predates these fields.
export const getSelectedAddOns = (ele) => {
  const hasHaulOrMove = ele?.haulOption === 'y' || ele?.moveOption === 'y';
  const candidates = [
    ...(ele?.hasParts && Array.isArray(ele?.installationAddOns) ? ele.installationAddOns : []),
    ...(hasHaulOrMove && Array.isArray(ele?.haulAwayAddOns) ? ele.haulAwayAddOns : [])
  ];
  return candidates
    .filter((addOn) => (addOn?.type === ADD_ON_PART || addOn?.type === ADD_ON_SERVICE) && addOn?.configAttr)
    .map((addOn) => ({
      sku: String(addOn.configAttr),
      price: addOn.price
    }));
};

// The add-ons the product page widget sent with its add to cart, as cart.add-to-cart's
// cartReqParams.selectedAddOns: a JSON string of { [itemId]: { protection, installation, haulAway } }.
// The widget's atc-hdpp-widget-test payload doesn't carry them. Returns {} when none were sent.
export const getCartReqAddOns = (cartReqParams) => {
  const params = Array.isArray(cartReqParams) ? cartReqParams : [cartReqParams];
  return params.reduce((addOns, param) => {
    let selected = param?.selectedAddOns;
    if (typeof selected === 'string') {
      try {
        selected = JSON.parse(selected);
      } catch (e) {
        selected = null;
      }
    }
    return selected && typeof selected === 'object' ? { ...addOns, ...selected } : addOns;
  }, {});
};

// selectedAddOns from the last cart.add-to-cart, read by the atc events that follow it
let cartAddOns = {};

// The element with the plan, parts and haul/move AddOns sent with the cart add filled in,
// for drawer payloads that don't carry them. Fields the element does send win.
const withCartAddOns = (ele) => {
  const selected = cartAddOns[ele.info.item.product.itemId] || {};
  return {
    ...ele,
    protectionPlan: ele.protectionPlan ?? selected.protection,
    installationAddOns: ele.installationAddOns ?? selected.installation,
    haulAwayAddOns: ele.haulAwayAddOns ?? selected.haulAway
  };
};

// The add-ons the drawer saved for each cart line, from its updateCart data-sources response:
// { [lineItemId]: { protection, installation, haulAway } } in the drawer's AddOn shape, with no plan and
// empty lists for a line saved with nothing selected. The ATC overlay's atc events send only flags; the
// drawer saves its selections with updateCart after them. Only lines touched by this save request
// (variables.cartRequest.items.delivery[]) are marked as saved; prices and selections come from the response.
// Returns {} for any other response.
export const getUpdateCartSelections = (output) => {
  const items = output?.data?.updateCart?.items;
  if (!Array.isArray(items)) {
    return {};
  }
  const delivery = output?.variables?.cartRequest?.items?.delivery;
  const requestedIds = Array.isArray(delivery)
    ? new Set(delivery.map((item) => item?.id).filter(Boolean))
    : null;

  const targetItems = requestedIds
    ? items.filter((item) => requestedIds.has(item?.id))
    : items;

  return targetItems.reduce((selections, item) => {
    if (!item?.id) {
      return selections;
    }
    const addOns = (item?.product?.addons || []).filter((addOn) => addOn?.selected && addOn?.configAttr);
    // updateCart types are upper case (PART, SERVICE)
    const ofType = (type) => addOns
      .filter((addOn) => String(addOn.type).toLowerCase() === type.toLowerCase())
      .map((addOn) => ({ ...addOn, type }));
    return {
      ...selections,
      [item.id]: {
        protection: addOns.find((addOn) => addOn.category === 'protectionPlan'),
        installation: ofType(ADD_ON_PART),
        haulAway: ofType(ADD_ON_SERVICE)
      }
    };
  }, {});
};

// updateCart selections by cart line id, from the ATC overlay's drawer
let drawerSelections = {};

// The element with the plan, parts and haul/move the drawer saved filled in. Fields already set win.
const withDrawerSelections = (ele) => {
  const selected = drawerSelections[ele.info.item.id] || {};
  return {
    ...ele,
    protectionPlan: ele.protectionPlan ?? selected.protection,
    installationAddOns: ele.installationAddOns ?? selected.installation,
    haulAwayAddOns: ele.haulAwayAddOns ?? selected.haulAway
  };
};

// The cart page's drawers keep tracking the anchor SKU (P&S tech spec use cases 6 and 8)
const isCartPage = () => digitalData?.page?.pageInfo?.pageType === 'checkout' || getState()?.pageType === CART;

// Cart-page events keep tracking the anchor SKU and skip enrichment from cached selections
const enrich = (ele) => (isCartPage() ? ele : withDrawerSelections(withCartAddOns(ele)));

const DRAWER_PAGE = /\/cart\/appliance-check-availability/;
// Keyed by the drawer's event bus, not its window: an iframe keeps the same window when it reloads,
// but each drawer page load brings a new bus
const watchedDrawerBuses = new WeakSet();

// Cart adds waiting for the drawer's updateCart, each with the cart lines it needs, in the order they fired
let heldCartAdds = [];
// updateCart follows Continue to Cart by about 2s; don't hold the parts and services cart add much longer
const DRAWER_UPDATE_CART_WAIT = 5000;

const sendHeld = (entries) => {
  heldCartAdds = heldCartAdds.filter((entry) => !entries.includes(entry));
  entries.forEach((entry) => {
    clearTimeout(entry.timer);
    try {
      entry.send();
    } catch (e) {
      // guard each send so one failing hit doesn't drop the rest of the batch or crash the drawer listener
    }
  });
};

const hasSavedLines = (lineIds) => lineIds.every((lineId) => drawerSelections[lineId]);

// a package or bundle drawer can save its lines separately, so release only the cart adds whose lines are saved
const sendSavedHeldCartAdds = () => sendHeld(heldCartAdds.filter((entry) => hasSavedLines(entry.lineIds)));

const sendAllHeldCartAdds = () => sendHeld([...heldCartAdds]);

// The ATC overlay's drawer runs on its own page in an iframe, with its own event bus. Listen there
// for its updateCart, and send what's held if the drawer closes first. Returns false when there's no
// drawer bus to wait on or if cross-origin access throws.
const watchDrawer = () => {
  try {
    const drawer = [...document.querySelectorAll('iframe')].find((frame) => DRAWER_PAGE.test(frame.src))?.contentWindow;
    const bus = drawer?.LIFE_CYCLE_EVENT_BUS;
    if (!bus) {
      return false;
    }
    if (!watchedDrawerBuses.has(bus)) {
      watchedDrawerBuses.add(bus);
      drawerSelections = {};
      const saveSelections = ({ output } = {}) => {
        const selections = getUpdateCartSelections(output);
        if (Object.keys(selections).length) {
          drawerSelections = { ...drawerSelections, ...selections };
          sendSavedHeldCartAdds();
        }
      };
      bus.on('data-sources.data', saveSelections);
      bus.lifeCycle?.on('data-sources.data', saveSelections);
      // the drawer closing ends its cart-add session: send what's held, then forget what it saved and
      // the add-ons sent with the cart add, so a later session can't reuse either
      drawer.addEventListener?.('pagehide', () => {
        sendAllHeldCartAdds();
        drawerSelections = {};
        cartAddOns = {};
      });
    }
    return true;
  } catch (e) {
    return false;
  }
};

// Send a cart add now, or hold it for the drawer's updateCart of the cart lines (lineIds) whose
// selections the payload doesn't carry. The HDPP cart add waits for the drawer to save (it fires before
// any cart call); the parts and services cart add waits at most waitLimit.
const sendOrHold = (send, { lineIds, waitLimit }) => {
  if (isCartPage()) {
    send();
    return;
  }
  // Detect the current drawer before trusting cached lines: a newly opened drawer resets the selections
  // an earlier drawer saved. With no drawer to watch, drop them so the hit falls back to the anchor SKU.
  if (!watchDrawer()) {
    drawerSelections = {};
    send();
    return;
  }
  if (!lineIds.length || hasSavedLines(lineIds)) {
    send();
    return;
  }
  const entry = { lineIds, send };
  if (waitLimit) {
    entry.timer = setTimeout(() => sendHeld([entry]), waitLimit);
  }
  heldCartAdds.push(entry);
};

export const partsAndServicesDrawer = () => {
  window.LIFE_CYCLE_EVENT_BUS.on('cart.add-to-cart', ({ output } = {}) => {
    cartAddOns = getCartReqAddOns(output?.cartReqParams);
  });

  // nothing held is lost if the page goes before the drawer saves
  window.addEventListener?.('pagehide', sendAllHeldCartAdds);

  window.LIFE_CYCLE_EVENT_BUS.on('parts-and-services-drawer.displayed-hdpp', () => {

    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: HDPP_OPTIONS_DRAWER
      }
    };
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });
  
  // the BOPIS drawer's parts and services step, on load (Appliance PIP > Out of Stock on Pip)
  window.LIFE_CYCLE_EVENT_BUS.on('parts-and-services-drawer.displayed-bopis-parts-and-services', () => {

    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: BOPIS_PARTS_AND_SERVICES
      }
    };
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  window.LIFE_CYCLE_EVENT_BUS.on('parts-and-services-drawer.displayed-installation-haulaway', ({ output }) => {
    const items = [];

    output.forEach((ele) => {

      let itemInfo = {
        fulfillment: {
          method: APPL,
          zip: ele.info.fulfillmentLocation
        },
        price: {
          basePrice: ele.info.item.product.pricing.original
        },
        productInfo: {
          sku: ele.info.item.product.itemId,
          availabilityStatus: {
            inStockAppl: "true",
          },
          isPackage: ele.isPackage ? "true" : "false",
          isBundle: ele.isBundle ? "true" : "false",
        },
        quantity: '1'
      };

      items.push(itemInfo);
    });

    const ddoEvent1 = {
      category: {
        primaryCategory: STASH
      },
      eventInfo: {
        eventName: CART_ADD_LOCATION
      },
      eventmode: STASH,
      stash: {
        component: NOT_APPLICABLE,
        pageType: PARTS_AND_SERVICES,
        section: NOT_APPLICABLE,
        sharedSection: HDPP_CART_ADD,
        target: ATC
      }
    };

    let OVERLAY_EVENT_NAME = '';
    if (output.length === 1) {
      OVERLAY_EVENT_NAME = APPL_INSTALL_AND_HAUL_CART_ADD;
    } else if (output.length >1) {
      if(output[0].isPackage) {
        OVERLAY_EVENT_NAME = PACKAGE_APPL_P_S_CART_ADD;
      } else if (output[0].isBundle) {
        OVERLAY_EVENT_NAME = BUNDLE_APPL_P_S_CART_ADD;
      }
    }

    const ddoEvent2 = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: OVERLAY_EVENT_NAME
      },
      item: items,
      overlayLocation: PARTS_AND_SERVICES,
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent1);
    digitalData.event.push(ddoEvent2);
  });

  window.LIFE_CYCLE_EVENT_BUS.on('parts-and-services-drawer.atc-hdpp', ({ output }) => {

    const send = () => {
      const psOverlays = [];
      const items = [];

      output.forEach((ele) => {
        let protection = {
          anchorSku: ele.info.item.product.itemId,
          hdppOption: ele.hasProtection ? 'y' : 'n',
          partsOption: ele.hasParts ? 'y' : 'n',
        };

        // Track the added HDPP plan rather than the anchor SKU, from the drawer, the add-ons sent with
        // the cart add or the drawer's updateCart; fall back to the anchor when no plan was added or on the cart page.
        const plan = isCartPage() ? null : getSelectedProtectionPlan(enrich(ele));
        if (plan) {
          // lets productString attach eVar68 to the plan's product row
          protection.addOnSkus = [plan.sku];
        }

        let itemInfo = {
          fulfillment: {
            method: APPL,
            zip: ele.info.item.fulfillmentLocation
          },
          price: {
            basePrice: plan ? plan.price : ele.info.item.product.pricing.original
          },
          productInfo: {
            sku: plan ? plan.sku : ele.info.item.product.itemId
          },
          quantity: '1'
        };
        psOverlays.push(protection);
        items.push(itemInfo);
      });

      const ddoEvent1 = {
        category: {
          primaryCategory: STASH
        },
        eventInfo: {
          eventName: CART_ADD_LOCATION
        },
        eventmode: STASH,
        stash: {
          component: NOT_APPLICABLE,
          pageType: PARTS_AND_SERVICES,
          section: NOT_APPLICABLE,
          sharedSection: HDPP_CART_ADD,
          target: ATC
        }
      };

      const ddoEvent2 = {
        category: {
          primaryCategory: ADD_TO_CART_CLICK
        },
        eventInfo: {
          eventName: CART_ADD
        },
        item: items,
        attributes: {
          psOverlay: psOverlays
        }
      };

      digitalData.event = digitalData.event || [];
      digitalData.event.push(ddoEvent1);
      digitalData.event.push(ddoEvent2);
    };

    // the ATC overlay sends no plan; its drawer saves the plan with updateCart once the customer
    // continues to the cart or keeps shopping
    sendOrHold(send, {
      lineIds: output
        .filter((ele) => ele.hasProtection && !getSelectedProtectionPlan(withCartAddOns(ele)))
        .map((ele) => ele.info.item.id)
    });
  });

 window.LIFE_CYCLE_EVENT_BUS.on('parts-and-services-drawer.atc-hdpp-widget-test', ({ output }) => {

    const psOverlays = [];
    const items = [];

    output.forEach((ele) => {

      const buildItem = (sku, basePrice) => ({
        fulfillment: {
          method: APPL,
          zip: ele.info.item.fulfillmentLocation
        },
        price: {
          basePrice
        },
        productInfo: {
          sku
        },
        quantity: '1'
      });

      let psOverlay = {
        anchorSku: ele.info.item.product.itemId,
        hdppOption: ele.hasProtection ? 'y' : 'n',
        installOption: ele.hasInstallation ? 'y' : 'n',
        partsOption: ele.hasParts ? 'y' : 'n',
        haulOption: ele.haulOption,
        moveOption: ele.moveOption
      };

      // Track the added HDPP plan, parts and haul/move services rather than the anchor SKU, from the
      // add-ons sent with the cart add; fall back to the anchor when nothing was added.
      const withAddOns = withCartAddOns(ele);
      const plan = getSelectedProtectionPlan(withAddOns);
      const addOns = [...(plan ? [plan] : []), ...getSelectedAddOns(withAddOns)];

      if (addOns.length) {
        addOns.forEach((addOn) => items.push(buildItem(addOn.sku, addOn.price)));
        // lets productString attach eVar68 and eVar126 to each add-on's product row
        psOverlay.addOnSkus = addOns.map((addOn) => addOn.sku);
      } else {
        items.push(buildItem(ele.info.item.product.itemId, ele.info.item.product.pricing.original));
      }
      psOverlays.push(psOverlay);
    });

    // the widget's cart add is the last to read the selected add-ons
    cartAddOns = {};

    const ddoEvent1 = {
      category: {
        primaryCategory: STASH
      },
      eventInfo: {
        eventName: CART_ADD_LOCATION
      },
      eventmode: STASH,
      stash: {
        component: NOT_APPLICABLE,
        pageType: PARTS_AND_SERVICES,
        section: NOT_APPLICABLE,
        sharedSection: HDPP_CART_ADD,
        target: ATC
      }
    };

    const ddoEvent2 = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: APPL_HDPP_CART_ADD
      },
      item: items,
      attributes: {
        psOverlay: psOverlays
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent1);
    digitalData.event.push(ddoEvent2);
  });

  window.LIFE_CYCLE_EVENT_BUS.on('parts-and-services-drawer.atc-installation-haulaway', ({ output }) => {

    const send = () => {
      const items = [];
      const psOverlays = [];

      output.forEach((ele) => {

        const buildItem = (sku, basePrice) => ({
          fulfillment: {
            method: APPL,
            zip: ele.info.fulfillmentLocation
          },
          price: {
            basePrice
          },
          productInfo: {
            sku
          },
          quantity: '1'
        });

        let psOverlay = {
          anchorSku: ele.info.item.product.itemId,
          installOption: ele.hasInstallation ? 'y' : 'n',
          partsOption: ele.hasParts ? 'y' : 'n',
          haulOption: ele.haulOption,
          moveOption: ele.moveOption
        };

        // Track each added part and haul/move service rather than the anchor SKU, from the drawer, the
        // add-ons sent with the cart add or the drawer's updateCart; fall back to the anchor when nothing was added or on the cart page.
        const addOns = isCartPage() ? [] : getSelectedAddOns(enrich(ele));
        if (addOns.length) {
          addOns.forEach((addOn) => items.push(buildItem(addOn.sku, addOn.price)));
          // lets productString attach eVar126 to each add-on's product row
          psOverlay.addOnSkus = addOns.map((addOn) => addOn.sku);
        } else {
          items.push(buildItem(ele.info.item.product.itemId, ele.info.item.product.pricing.original));
        }
        psOverlays.push(psOverlay);
      });

      const ddoEvent1 = {
        category: {
          primaryCategory: STASH
        },
        eventInfo: {
          eventName: CART_ADD_LOCATION
        },
        eventmode: STASH,
        stash: {
          component: NOT_APPLICABLE,
          pageType: PARTS_AND_SERVICES,
          section: NOT_APPLICABLE,
          sharedSection: HDPP_CART_ADD,
          target: ATC
        }
      };

      const ddoEvent2 = {
        category: {
          primaryCategory: ADD_TO_CART_CLICK
        },
        eventInfo: {
          eventName: CART_ADD
        },
        item: items,
        attributes: {
          psOverlay: psOverlays
        }
      };

      digitalData.event = digitalData.event || [];
      digitalData.event.push(ddoEvent1);
      digitalData.event.push(ddoEvent2);
    };

    // the ATC overlay sends only flags; its drawer saves the parts and haul/move with updateCart about
    // 2s after this fires
    const hasAddOnFlags = (ele) => ele.hasParts || ele.haulOption === 'y' || ele.moveOption === 'y';
    sendOrHold(send, {
      lineIds: output
        .filter((ele) => hasAddOnFlags(ele) && !getSelectedAddOns(withCartAddOns(ele)).length)
        .map((ele) => ele.info.item.id),
      waitLimit: DRAWER_UPDATE_CART_WAIT
    });
  });

};